import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Mono, PageBody, PageHeader, StackedList } from "../ui/Field";

type Sub = { id: string; page_id: string | null; payload: string; webhook_status: number | null; webhook_error: string | null; ip: string | null; user_agent: string | null; submitted_at: number };

export default function FormSubmissions() {
  const { slug, block } = useParams();
  const [items, setItems] = useState<Sub[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = () => api.get<{ items: Sub[] }>(`/api/sites/${slug}/forms/${block}/submissions`)
    .then((r) => setItems(r.items))
    .catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug && block) load(); /* eslint-disable-next-line */ }, [slug, block]);

  async function replay(id: string) {
    try { await api.post(`/api/sites/${slug}/forms/${block}/submissions/${id}/replay`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <Link to={`/sites/${slug}/forms`} className="text-[var(--color-muted)] hover:text-[var(--color-ink)] text-[12px] no-underline">← Forms</Link>
      <PageHeader
        eyebrow="№ Form · Submissions"
        title={<>Submissions</>}
        description={<>Form block <Mono>{block}</Mono>. Inspect payloads and replay failed webhook deliveries.</>}
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="X1" title={`Entries (${items.length})`} bodyClassName={items.length === 0 ? "p-5" : "p-0"}>
        {items.length === 0 ? (
          <EmptyState title="No submissions." description="When users submit, entries collect here." />
        ) : (
          <StackedList className="px-5">
            {items.map((s) => {
              const ok = s.webhook_status && s.webhook_status >= 200 && s.webhook_status < 400;
              return (
                <li key={s.id} className="py-3">
                  <div className="flex items-center justify-between gap-3">
                    <button
                      onClick={() => setOpen((o) => o === s.id ? null : s.id)}
                      className="bg-transparent border-0 cursor-pointer text-left flex-1 text-[var(--color-ink)] hover:text-[var(--color-accent)] transition-colors"
                    >
                      <div className="text-[13px]">{new Date(s.submitted_at).toLocaleString()}</div>
                      <div className="text-[12px] text-[var(--color-muted)] font-mono truncate mt-0.5">{summary(s.payload)}</div>
                    </button>
                    <Badge tone={ok ? "success" : s.webhook_status ? "error" : "neutral"}>
                      {s.webhook_status ? `HTTP ${s.webhook_status}` : s.webhook_error ?? "no webhook"}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => replay(s.id)}>Replay</Button>
                  </div>
                  {open === s.id && (
                    <pre className="bg-[var(--color-paper-2)] border border-[var(--color-rule)] p-3 rounded-[var(--radius-sm)] mt-2 text-[12px] overflow-auto font-mono text-[var(--color-ink-2)]">
                      {JSON.stringify(JSON.parse(s.payload), null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}

function summary(payload: string): string {
  try {
    const obj = JSON.parse(payload) as Record<string, unknown>;
    return Object.entries(obj).slice(0, 3).map(([k, v]) => `${k}=${String(v).slice(0, 30)}`).join(", ");
  } catch {
    return payload.slice(0, 80);
  }
}
